import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useRouter } from "next/router";
import { BottomSheet } from "../../../components/modals/bottom-sheet/bottom-sheet";
import {
  Product,
  ProductUnit,
} from "../../../stores/product-store/product-store.model";
import { numericToStringThousands } from "../../../utils/number";
import {
  CartItem,
  mapToCartItem,
} from "../../../stores/cart-store/cart-store.model";
import { MAX_BUY_COUNT } from "../../../utils/constants";
import { useStores } from "../../../stores";
import { trackEvents } from "../../../utils/analytics";
import { postMessageToBuyerApp } from "../../../utils/post-message";
import { SellerPageRouter } from "../page-router.props";

interface BuyProductSheetProps {
  product: Product;
  cartItem?: { index: number; item: CartItem };
  onRemoveCartItem?: (cartId: string) => void;
  onDismiss: () => void;
  onSuccess?: () => void;
}

export const BuyProductSheet: React.FC<BuyProductSheetProps> = observer(
  ({ product, cartItem, onRemoveCartItem, onDismiss, onSuccess }) => {
    const router: SellerPageRouter = useRouter();
    const { cartStore, sellerStore } = useStores();

    const [selectedUnit, setSelectedUnit] = useState<ProductUnit>(null);
    const [buyCount, setBuyCount] = useState<number>(1);

    const navUnitsRef = useRef<HTMLDivElement>();

    const isOpen = useMemo(
      () => product?.baseUnits?.some((unit) => unit.id),
      [product?.baseUnits]
    );
    const currentUnit = useMemo(() => {
      return (
        product?.productUnits?.length &&
        selectedUnit &&
        product?.productUnits.find(
          (unit) => unit.unit === selectedUnit.unit && buyCount >= unit.minQty
        )
      );
    }, [buyCount, product?.productUnits, selectedUnit]);
    const unitPrice = useMemo(() => {
      return currentUnit
        ? currentUnit.discountPrice > 0
          ? currentUnit.discountPrice
          : currentUnit.price
        : 0;
    }, [currentUnit]);

    useEffect(() => {
      if (isOpen) {
        setSelectedUnit(product?.baseUnits[0]);
        setBuyCount(1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // effect to handle edit mode
    useEffect(() => {
      if (isOpen && cartItem) {
        setBuyCount(cartItem?.item?.qty);
        setSelectedUnit(cartItem?.item?.selectedUnit);
      }
    }, [isOpen, cartItem]);

    const onSlideUnits = (direction: "prev" | "next") => {
      let scrollCompleted = 0;
      const slideVal = setInterval(function () {
        if (direction === "prev") navUnitsRef.current.scrollLeft -= 10;
        else navUnitsRef.current.scrollLeft += 10;
        scrollCompleted += 10;
        if (scrollCompleted >= 100) window.clearInterval(slideVal);
      }, 10);
    };

    const handleChangeBuyCount = (val: string) => {
      let changeVal = Number(val);

      if ((changeVal <= 0 || isNaN(changeVal)) && val !== "") {
        return;
      }

      if (changeVal > MAX_BUY_COUNT) {
        changeVal = MAX_BUY_COUNT;
      }
      setBuyCount(changeVal);
    };

    const handleDecrementBuyCount = () => {
      const decrementVal = buyCount - 1;
      if (decrementVal <= 0) {
        setBuyCount(0);
      } else {
        setBuyCount(decrementVal);
      }
    };

    const handleIncrementBuyCount = () => {
      const incrementVal = buyCount + 1;
      if (incrementVal >= MAX_BUY_COUNT) {
        setBuyCount(MAX_BUY_COUNT);
      } else {
        setBuyCount(incrementVal);
      }
    };

    const handleConfirm = () => {
      const newCartItem = mapToCartItem(product, selectedUnit, buyCount);
      if (cartItem) cartStore?.updateItemToCart(cartItem.index, newCartItem);
      else cartStore?.addItemToCart(newCartItem);
      if (onSuccess) onSuccess();
      else onDismiss();

      if (cartItem && sellerStore?.sellerProfile?.sellerJopan) {
        trackEvents({
          sellerUsername: sellerStore?.sellerProfile?.User?.username,
          eventName: "produk_atc",
          eventCategory: "order",
          eventLabel: "add to cart",
          eventValue: {
            item: cartItem?.item,
            counter: buyCount,
          },
          trackers: ["GTM", "AF", "ME"],
        });
      }

      if (router.query.fromapp) postMessageToBuyerApp("cart-product-change");
    };

    const handleRemoveItem = () => {
      onDismiss();
      if (cartItem) onRemoveCartItem(cartItem?.item?.cartId);
    };

    const renderHeader = () => {
      return (
        <div className="flex items-center justify-between pb-4 text-label-lg font-bold">
          <div className="uppercase">
            {buyCount} {selectedUnit?.unit}
          </div>
          <div>Rp{numericToStringThousands(unitPrice)}</div>
        </div>
      );
    };

    const renderUnitOptions = () => {
      return (
        <div className="flex items-center justify-center py-4">
          {product?.baseUnits?.length > 5 && (
            <FiChevronLeft
              className="shrink-0 cursor-pointer size-5"
              onClick={() => onSlideUnits("prev")}
            />
          )}
          <div
            ref={navUnitsRef}
            className="flex items-center overflow-auto no-scrollbar"
          >
            {product?.baseUnits?.map((unit) => {
              return (
                <button
                  key={unit.id}
                  onClick={() => setSelectedUnit(unit)}
                  className={classNames(
                    "shrink-0 py-3 px-4 mr-4 last:mr-0 rounded-md uppercase cursor-pointer select-none hover:bg-opacity-95 font-bold",
                    selectedUnit?.unit === unit.unit
                      ? "text-custombg border border-white bg-primary"
                      : "border border-primary text-primary active:bg-primary-50"
                  )}
                  id="qty-sheet-unit-item-btn"
                  data-testid="qty-sheet-unit-item-btn"
                >
                  {unit.unit}
                </button>
              );
            })}
          </div>
          {product?.baseUnits?.length > 5 && (
            <FiChevronRight
              className="shrink-0 cursor-pointer size-5"
              onClick={() => onSlideUnits("next")}
            />
          )}
        </div>
      );
    };

    const renderQtySpinner = () => {
      return (
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center border rounded-lg overflow-hidden border-primary">
            <button
              className="size-12 p-3 text-label-xl text-center cursor-pointer bg-primary select-none active:bg-primary-50 text-custombg"
              onClick={handleDecrementBuyCount}
              id="qty-sheet-qty-dec-btn"
              data-testid="qty-sheet-qty-dec-btn"
            >
              -
            </button>
            <input
              className="text-center bg-gray-100 w-20 py-3 outline-none"
              value={buyCount}
              onChange={(event) => handleChangeBuyCount(event.target.value)}
              id="qty-sheet-qty-input"
              data-testid="qty-sheet-qty-input"
            />
            <button
              className="size-12 p-3 text-label-xl text-center cursor-pointer bg-primary select-none active:bg-primary-50 text-custombg"
              onClick={handleIncrementBuyCount}
              id="qty-sheet-qty-inc-btn"
              data-testid="qty-sheet-qty-inc-btn"
            >
              +
            </button>
          </div>
        </div>
      );
    };

    const renderTotal = () => {
      return (
        <div className="flex justify-between text-label-lg font-bold pt-4">
          <div>Total</div>
          <div>Rp{numericToStringThousands(unitPrice * buyCount)}</div>
        </div>
      );
    };

    const renderBtn = () => {
      return (
        <button
          onClick={() =>
            buyCount > 0
              ? handleConfirm()
              : cartItem
                ? handleRemoveItem()
                : onDismiss()
          }
          className="px-6 py-3 w-full text-center text-custombg font-bold rounded select-none hover:bg-opacity-95 bg-primary"
          id={
            buyCount > 0
              ? cartItem
                ? "product-item-bottom-sheet-change-btn"
                : "product-item-bottom-sheet-buy-btn"
              : cartItem
                ? "product-item-bottom-sheet-remove-btn"
                : "product-item-bottom-sheet-cancel-btn"
          }
          data-testid={
            buyCount > 0
              ? cartItem
                ? "product-item-bottom-sheet-change-btn"
                : "product-item-bottom-sheet-buy-btn"
              : cartItem
                ? "product-item-bottom-sheet-remove-btn"
                : "product-item-bottom-sheet-cancel-btn"
          }
        >
          {buyCount > 0
            ? cartItem
              ? "UBAH"
              : "BELI"
            : cartItem
              ? "HAPUS"
              : "BATAL"}
        </button>
      );
    };

    return (
      <BottomSheet visible={isOpen} onDismiss={onDismiss}>
        {renderHeader()}
        {renderUnitOptions()}
        {renderQtySpinner()}
        {renderTotal()}
        <hr />
        {renderBtn()}
      </BottomSheet>
    );
  }
);
